import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ApolloClient, gql } from 'apollo-boost';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import TrueImpactButton, {
  TrueImpactButtonWithLoading,
} from 'shared/design-system/components/TrueImpactButton';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { useClient, removeConnections } from 'shared/apollo';
import { useProgram } from 'shared/providers/ProgramProvider';
import { updateProgram } from 'shared/components/WelcomeBack/api';
import ContributionEligibilitySelector, {
  ContributionEligibility,
  asContributionEligibility,
} from '../ContributionEligibility/ContributionEligibilitySelector';
import { getAllNPOUsers, NPOUser } from '../NewProgram/api';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};

  .field-label {
    margin-bottom: ${SPACERS.tiny};
  }
  .actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${SPACERS.tiny};
  }
  .error {
    color: ${COLORS.critical};
  }
`;

const programDetailsQuery = gql`
  query programDetails($id: ID) {
    allPrograms(filters: { id: $id }) {
      edges {
        node {
          dbId
          name
          managerId
          contributionEligibility
        }
      }
    }
  }
`;

interface ProgramDetails {
  managerId: number | null;
  contributionEligibility: ContributionEligibility | null;
}

const fetchProgramDetails = async (
  client: ApolloClient<object>,
  programId: number,
): Promise<ProgramDetails> => {
  const res = await client.query<{
    allPrograms: {
      edges: {
        node: {
          dbId: number;
          name: string;
          managerId: number | null;
          contributionEligibility: string | null;
        };
      }[];
    };
  }>({
    query: programDetailsQuery,
    variables: { id: programId },
    fetchPolicy: 'network-only',
  });
  const node = res.data.allPrograms.edges[0]?.node;
  return {
    managerId: node?.managerId ?? null,
    contributionEligibility:
      asContributionEligibility(node?.contributionEligibility) ?? null,
  };
};

interface ProgramProfileFormProps {
  /** Called after a successful save. */
  onSaved?: () => void;
  /** Called when the user clicks Cancel. */
  onCancel?: () => void;
  /** When true, load users/details immediately (used when hosted inline
   *  with no "expand" trigger of its own). Defaults to true. */
  autoLoad?: boolean;
}

/**
 * Just the fields + save/cancel actions — no outer card / header. Used
 * both by the legacy `ProgramProfileEditor` (which wraps it in its own
 * collapsible card) and by `SidebarIdentity` (which embeds it inline
 * behind an "Edit details" toggle so the program identity card doesn't
 * repeat itself). Encapsulates its own loading + dirty tracking so hosts
 * only wire up open/close.
 */
export const ProgramProfileForm: React.FC<ProgramProfileFormProps> = ({
  onSaved,
  onCancel,
  autoLoad = true,
}) => {
  const client = useClient();
  const {
    programId,
    programName,
    updateProgramName,
    reloadProgram,
  } = useProgram();

  const [users, setUsers] = useState<NPOUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(programName);
  const [managerId, setManagerId] = useState<number | null>(null);
  const [
    eligibility,
    setEligibility,
  ] = useState<ContributionEligibility | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState<{
    name: string;
    managerId: number | null;
    eligibility: ContributionEligibility | null;
  } | null>(null);

  useEffect(() => {
    setName(programName);
  }, [programName]);

  useEffect(() => {
    if (!autoLoad) return;
    if (!client || programId == null) return;
    if (initialSnapshot) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getAllNPOUsers(client),
      fetchProgramDetails(client, programId),
    ])
      .then(([usersRes, details]) => {
        if (cancelled) return;
        const flat = removeConnections(usersRes.data) as
          | { allUsers?: NPOUser[] }
          | undefined;
        setUsers(Array.isArray(flat?.allUsers) ? flat!.allUsers : []);
        setManagerId(details.managerId);
        setEligibility(details.contributionEligibility);
        setInitialSnapshot({
          name: programName,
          managerId: details.managerId,
          eligibility: details.contributionEligibility,
        });
      })
      .catch(e => {
        if (cancelled) return;
        setError((e as Error)?.message || 'Unable to load program details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [autoLoad, client, programId]);

  const selectedManager = useMemo(
    () => users.find(u => u.userId === managerId),
    [users, managerId],
  );

  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    return (
      name.trim() !== initialSnapshot.name ||
      managerId !== initialSnapshot.managerId ||
      eligibility !== initialSnapshot.eligibility
    );
  }, [name, managerId, eligibility, initialSnapshot]);

  const handleCancel = () => {
    if (initialSnapshot) {
      setName(initialSnapshot.name);
      setManagerId(initialSnapshot.managerId);
      setEligibility(initialSnapshot.eligibility);
    }
    setError(null);
    onCancel?.();
  };

  const handleSave = async () => {
    if (!client || programId == null) return;
    if (!name.trim()) {
      setError('Program name is required.');
      return;
    }
    if (managerId == null) {
      setError('Please select a program manager.');
      return;
    }
    if (eligibility == null) {
      setError('Please select a contribution eligibility.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const nameChanged = name.trim() !== (initialSnapshot?.name ?? '');
      await updateProgram(
        client,
        programId,
        managerId,
        nameChanged ? name.trim() : undefined,
        undefined,
        eligibility ?? undefined,
      );
      if (nameChanged) {
        await updateProgramName(name.trim());
      } else {
        await reloadProgram();
      }
      setInitialSnapshot({
        name: name.trim(),
        managerId,
        eligibility,
      });
      onSaved?.();
    } catch (e) {
      setError((e as Error)?.message || 'Unable to save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Body>
      {loading && !initialSnapshot ? (
        <TrueImpactText variant="body-small" color="greyDark">
          Loading…
        </TrueImpactText>
      ) : (
        <>
          <div>
            <TrueImpactText variant="body-small-bold" className="field-label">
              Program name
            </TrueImpactText>
            <TrueImpactTextfield
              value={name}
              onChange={setName}
              placeholder="Program name"
            />
          </div>
          <div>
            <TrueImpactText variant="body-small-bold" className="field-label">
              Reporting manager
            </TrueImpactText>
            <TrueImpactDropdown
              value={selectedManager}
              options={users}
              getId={u => u.userId}
              getLabel={u => `${u.firstName} ${u.lastName} (${u.email})`}
              native={true}
              onChange={u => setManagerId(u ? u.userId : null)}
              onClear={undefined}
              getItemDisability={undefined}
              name="Select program manager"
            />
          </div>
          <ContributionEligibilitySelector
            value={eligibility}
            onChange={setEligibility}
          />
          {error && (
            <TrueImpactText variant="body-small" className="error">
              {error}
            </TrueImpactText>
          )}
          <div className="actions">
            <TrueImpactButton
              variant="secondary-small"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </TrueImpactButton>
            <TrueImpactButtonWithLoading
              variant="primary-small"
              onClick={handleSave}
              disabled={saving || !isDirty}
              isLoading={saving}
            >
              Save changes
            </TrueImpactButtonWithLoading>
          </div>
        </>
      )}
    </Body>
  );
};
